/**
 * usePotPeriodState
 * Custom hook for managing pot period selection via URL params + Zustand store.
 * URL is the primary source of truth; the store persists the last period across
 * tab switches (SubNavTabs strips query params on navigation).
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useFinanceUIStore } from '../stores/financeUIStore';

/**
 * Hook for managing pot period selection state via URL query params.
 * Uses potPeriodId as the URL parameter (single source of truth).
 * Syncs to/from financeUIStore so the period survives tab switches.
 *
 * @returns {Object} - Selected period ID and stable setter callback
 *
 * @example
 * ```tsx
 * const { selectedPeriodId, setPeriodId } = usePotPeriodState();
 *
 * // URL: ?potPeriodId=abc-123
 * // selectedPeriodId = "abc-123"
 *
 * setPeriodId("def-456"); // Updates URL to ?potPeriodId=def-456
 * ```
 */
export function usePotPeriodState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lastPeriodId, setLastPeriodId } = useFinanceUIStore();

  // Derive period ID from URL (primary) or Zustand store (fallback).
  // The store fallback ensures the period survives navigation that strips
  // query params (SubNavTabs, breadcrumbs, success handlers).
  const selectedPeriodId = useMemo<string | null>(() => {
    const periodId = searchParams.get('potPeriodId');
    return periodId || lastPeriodId;
  }, [searchParams, lastPeriodId]);

  // On mount: if URL has no period but store has one, restore it to URL
  useEffect(() => {
    if (!selectedPeriodId && lastPeriodId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('potPeriodId', lastPeriodId);
      setSearchParams(newParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL period to store whenever it changes
  useEffect(() => {
    if (selectedPeriodId) {
      setLastPeriodId(selectedPeriodId);
    }
  }, [selectedPeriodId, setLastPeriodId]);

  // Stable callback — uses functional updater to avoid depending on searchParams,
  // which would create a new ref on every URL change and cause effect loops.
  const setPeriodId = useCallback(
    (periodId: string) => {
      setLastPeriodId(periodId);
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('potPeriodId', periodId);
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams, setLastPeriodId]
  );

  return { selectedPeriodId, setPeriodId };
}
